import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { getAccountingEntryById } from 'lib/api/accounting';
import CustomButton from 'lib/designSystem/Button';
import { LightTooltip } from 'lib/designSystem/WhiteTooltip';
import { formatDate, formatMoney } from 'lib/functions/utils';
import React from 'react';
import { useDispatch } from 'react-redux';

import { AccountingLine, MajorBookModalEntry } from '../../../../types';
import EntryDiffViewer, { CalculatedLine } from './EntryDiffViewer';

export interface GroupedLine {
  entryId: string;
  account: string;
  accountName: string;
  description: string;
  businessIdentifier: string;
  documentId: string;
  debit: number;
  credit: number;
  auxiliaryBusinessIdentifier: string;
}

export interface Entry {
  businessIdentifier: string;
  businessName: string;
  category: string;
  groupedLines: GroupedLine[];
  date: string;
  sourceKey?: string;
  isEdited?: boolean;
  writtenEntryId?: number;
}

export type ModalEntry = Entry | MajorBookModalEntry;

interface ModalLineDisplay {
  entryId: string;
  account: string;
  accountName: string;
  description: string;
  documentId: string;
  debit: number;
  credit: number;
  auxiliaryBusinessIdentifier: string;
}

function toModalLineDisplay(
  line: GroupedLine | AccountingLine,
): ModalLineDisplay {
  return {
    entryId: String((line as { entryId?: string | number }).entryId ?? ''),
    account: line.account,
    accountName: line.accountName,
    description: (line as { description?: string | null }).description ?? '',
    documentId: (line as { documentId?: string | null }).documentId ?? '',
    debit: line.debit,
    credit: line.credit,
    auxiliaryBusinessIdentifier:
      (line as { auxiliaryBusinessIdentifier?: string | null })
        .auxiliaryBusinessIdentifier ?? '-',
  };
}

interface EntryModalProps {
  entry?: ModalEntry;
  open: boolean;
  onClose: () => void;
  onEdit?: (entry: ModalEntry) => void;
  onSave?: (entry: ModalEntry) => void;
  saving?: boolean;
  readOnly?: boolean;
  allowSaving?: boolean;
}

const EntryModal: React.FC<EntryModalProps> = ({
  entry,
  open,
  onClose,
  onEdit,
  onSave,
  saving = false,
  readOnly = false,
  allowSaving = false,
}) => {
  const dispatch = useDispatch();
  const lines = entry?.groupedLines ?? [];
  const displayLines = lines.map((line) => toModalLineDisplay(line));
  const hasAuxiliaryData = displayLines.some(
    (d) => d.auxiliaryBusinessIdentifier !== '-',
  );
  const writtenEntryId = (entry as Entry)?.writtenEntryId;
  const {
    data: savedEntry,
    isLoading: loadingSavedEntry,
    error: savedEntryError,
  } = getAccountingEntryById(dispatch, writtenEntryId);

  // Convert displayLines to CalculatedLine format for diff viewer
  const calculatedLinesForDiff: CalculatedLine[] = displayLines.map((line) => ({
    account: line.account,
    accountName: line.accountName,
    description: line.description,
    documentId: line.documentId,
    debit: line.debit,
    credit: line.credit,
  }));

  const renderEntryDiffTooltip = () => {
    if (loadingSavedEntry) {
      return (
        <div style={{ padding: 16, textAlign: 'center' }}>
          <CircularProgress size={24} />
          <Typography variant="body2">Cargando comparacion...</Typography>
        </div>
      );
    }

    if (savedEntryError) {
      return (
        <div style={{ padding: 16, textAlign: 'center' }}>
          <Typography variant="body2" color="error">
            Error al cargar el asiento guardado
          </Typography>
        </div>
      );
    }

    if (!savedEntry) return null;

    return (
      <EntryDiffViewer
        savedEntry={savedEntry}
        calculatedLines={calculatedLinesForDiff}
      />
    );
  };
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Grid container spacing={2} alignItems="flex-start">
          <Grid item xs={12} md={8}>
            {!readOnly && (
              <Typography
                variant="h5"
                component="div"
                sx={{
                  fontWeight: 600,
                  color: '#2c3e50',
                  mb: 1,
                }}
              >
                Asiento #
                {String(
                  (lines[0] as { entryId?: string | number } | undefined)
                    ?.entryId ?? '',
                )}
              </Typography>
            )}
            <Typography
              variant="h5"
              component="div"
              sx={{
                fontWeight: 600,
                color: '#2c3e50',
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              {entry?.category}
              {allowSaving && (entry as Entry)?.writtenEntryId && (
                <LightTooltip
                  title={renderEntryDiffTooltip()}
                  placement="bottom-start"
                >
                  <Chip
                    icon={
                      loadingSavedEntry ? (
                        <CircularProgress size={14} />
                      ) : (
                        <InfoOutlinedIcon />
                      )
                    }
                    label="Ver diferencias"
                    size="small"
                    variant="outlined"
                    color={savedEntryError ? 'error' : 'info'}
                    sx={{ cursor: 'pointer' }}
                  />
                </LightTooltip>
              )}
            </Typography>

            <Typography
              variant="body1"
              component="div"
              sx={{
                color: '#7f8c8d',
                fontWeight: 500,
              }}
            >
              {entry?.businessName}
            </Typography>
            <Typography
              variant="body2"
              component="div"
              sx={{
                color: '#95a5a6',
                fontStyle: 'italic',
              }}
            >
              RUT: {entry?.businessIdentifier}
            </Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography
              variant="h6"
              component="div"
              sx={{
                textAlign: { xs: 'left', md: 'right' },
                color: '#2c3e50',
                fontWeight: 500,
              }}
            >
              {formatDate(new Date(entry?.date ?? ''), 'dd-mm-yyyy')}
            </Typography>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Cuenta</TableCell>
              <TableCell>Nombre de Cuenta</TableCell>
              <TableCell>Descripción</TableCell>
              {hasAuxiliaryData && <TableCell>Auxiliar</TableCell>}
              <TableCell>Documento</TableCell>
              <TableCell>Debe</TableCell>
              <TableCell>Haber</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayLines.map((line, lineIndex) => (
              <TableRow key={lineIndex}>
                <TableCell>{line.account}</TableCell>
                <TableCell>{line.accountName}</TableCell>
                <TableCell>{line.description}</TableCell>
                {hasAuxiliaryData && (
                  <TableCell
                    style={{
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {line.auxiliaryBusinessIdentifier}
                  </TableCell>
                )}
                <TableCell>{line.documentId}</TableCell>
                <TableCell>{formatMoney(line.debit)}</TableCell>
                <TableCell>{formatMoney(line.credit)}</TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell
                colSpan={hasAuxiliaryData ? 5 : 4}
                style={{
                  fontWeight: 'bold',
                  textAlign: 'left',
                }}
              >
                Totales:
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>
                {formatMoney(
                  displayLines.reduce((sum, line) => sum + line.debit, 0),
                )}
              </TableCell>
              <TableCell style={{ fontWeight: 'bold' }}>
                {formatMoney(
                  displayLines.reduce((sum, line) => sum + line.credit, 0),
                )}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions>
        {!readOnly && (
          <CustomButton
            color="primary"
            onClick={() => {
              if (entry && onEdit) {
                onEdit(entry);
              }
              onClose();
            }}
          >
            Editar
          </CustomButton>
        )}
        {allowSaving && (
          <CustomButton
            color="success"
            disabled={saving}
            onClick={() => {
              if (entry && onSave) {
                onSave(entry);
              }
            }}
          >
            {saving ? 'Guardando...' : 'Guardar'}
          </CustomButton>
        )}
        <CustomButton onClick={onClose} color="secondary">
          Cerrar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default EntryModal;
